import * as tf from "@tensorflow/tfjs-core";
import { setThreadsCount } from "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-webgl";

/* ================== CONSTANTS ================== */

const CLASS_LABELS = [
  "",
  "front_bumper",
  "front_door",
  "back_door",
  "front_glass",
  "fender",
  "qtr_panel",
  "window_glass",
  "running_board",
  "side_view_mirror",
  "headlight",
  "wheel",
  "back_bumper",
  "dicky",
  "back_glass",
  "taillight",
  "hood",
];

const CLASS_THRESHOLDS = new Float32Array(17).fill(0.7);

const customThresholds = {
  8: 0.3,
  5: 0.4,
  6: 0.4,
  2: 0.6,
  7: 0.4,
  11: 0.45,
  14: 0.4,
};

Object.entries(customThresholds).forEach(
  ([idx, val]) => (CLASS_THRESHOLDS[idx] = val)
);

/* ================== QUANTIZATION ================== */

const S_SCORE = 0.003807874396443367;
const S_BOX = 0.007213558070361614;
const Z_BOX = 45;
const S_CLASS = 0.05882352963089943;
const S_NUM = 0.09803921729326248;

/* ================== LOAD MODEL ================== */

export async function loadCarPartModel() {
  try {
    // Tell WASM to use multiple CPU cores for maximum speed
    const cores = navigator.hardwareConcurrency || 4;
    const threadsToUse = Math.max(1, Math.floor(cores / 2));
    setThreadsCount(threadsToUse);

    await tf.setBackend("webgl");
    await tf.ready();
    console.log(`Successfully initialized WebGL backend with ${threadsToUse} threads for int8 optimization`);
  } catch (error) {
    console.warn("WebGL failed to initialize. Falling back to WASM.", error);
    await tf.setBackend("wasm");
    await tf.ready();
    console.log("Successfully initialized WASM backend");
  }

  const model = await window.tflite.loadTFLiteModel("/model_int8.tflite");
  return model;
}

/* ================== PREPROCESS ================== */

/* ================== PREPROCESS ================== */

export const preprocessVideo = (canvas) => {
  return tf.tidy(() => {
    return tf.browser
      .fromPixels(canvas)
      .expandDims(0)
      .cast("int32"); // RESTORED INT32: The model needs this to map to 'uint8'
  });
};

/* ================== INFERENCE ================== */

export async function runCarPartDetection(model, inputTensor) {

  const result = model.predict(inputTensor);
  const outputs = Array.isArray(result) ? result : [result];
  const tensors = Object.values(outputs[0] || outputs);

  if (tensors.length !== 4) {
    cleanupTensors(outputs);
    // Note: inputTensor.dispose() is deliberately omitted here so StencilVideo.js doesn't crash
    return [];
  }

  const [scoresT, boxesT, numT, classT] = tensors;

  // =========================================================
  // OPTIMIZATION: Vectorize the math! 
  // This does all the heavy math on the hardware backend instantly
  // =========================================================
  const processedTensors = tf.tidy(() => {
    return [
      tf.mul(scoresT, S_SCORE),                                     // Dequantize Scores
      tf.mul(tf.sub(boxesT, Z_BOX), S_BOX),                         // Dequantize Boxes
      tf.round(tf.mul(numT, S_NUM)),                                // Dequantize Count
      tf.add(tf.round(tf.mul(classT, S_CLASS)), 1)                  // Dequantize Classes
    ];
  });

  const [dScoresT, dBoxesT, dNumT, dClassesT] = processedTensors;

  // ASYNC EXTRACTION: Download the pre-calculated data without freezing the UI
  const [scoresData, boxesData, numData, classData] = await Promise.all([
    dScoresT.data(),
    dBoxesT.data(),
    dNumT.data(),
    dClassesT.data()
  ]);

  const count = Math.min(numData[0], scoresData.length);
  const detections = [];

  // JS loop is now lightning fast because it only reads pre-calculated values
  for (let i = 0; i < count; i++) {
    const score = scoresData[i];
    if (score < 0.05) continue;

    const classId = classData[i];
    const threshold = CLASS_THRESHOLDS[classId] || 0.7;

    if (score >= threshold && CLASS_LABELS[classId]) {
      const b = i * 4;
      
      // Because we used vectorized math above, we just read the values directly now
      const ymin = boxesData[b];
      const xmin = boxesData[b + 1];
      const ymax = boxesData[b + 2];
      const xmax = boxesData[b + 3];

      detections.push({
        bbox: [
          xmin,
          ymin,
          xmax - xmin, // width
          ymax - ymin  // height
        ],
        class: classId,
        label: CLASS_LABELS[classId],
        score,
      });
    }
  }

  // Cleanup both sets of tensors to prevent memory leaks
  cleanupTensors(outputs);
  cleanupTensors(processedTensors);
  
  return detections;
}

/* ================== CLEANUP ================== */

function cleanupTensors(outputs) {
  outputs.forEach((t) => {
    if (t && t.dispose) t.dispose();
  });
}